import React, { useState, useRef, useCallback } from 'react';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonIcon,
  IonButton,
  IonCheckbox,
  IonReorderGroup,
  IonReorder,
  ItemReorderEventDetail,
  IonGrid,
  IonCard,
  IonSpinner,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import BasePageWithHeader, { TitleCard } from './BasePageWithHeader';
import { Shortcut } from '@rsmaids/shared/types/dashboard';
import styles from './ShortcutsEditModal.module.scss';
import { useTranslation } from 'react-i18next';

interface ShortcutsEditModalProps {
  onDismiss: () => void;
  activeShortcutIds: string[];
  allShortcuts: Shortcut[];
  categoryNames: Record<string, string>;
  onSave: (shortcuts: string[]) => Promise<void>;
}

export const ShortcutsEditModal: React.FC<ShortcutsEditModalProps> = ({
  onDismiss,
  activeShortcutIds: initialActiveShortcuts,
  allShortcuts,
  categoryNames,
  onSave,
}) => {
  const { t } = useTranslation();
  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const scrollContainerRef = useRef<HTMLIonGridElement>(null);
  const titleCardRef = useRef<HTMLDivElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
  const rafTick = useRef(false);

  const [activeShortcutIds, setActiveShortcutIds] = useState<string[]>(initialActiveShortcuts);
  const [isSaving, setIsSaving] = useState(false);

  const applyProgress = useCallback((progress: number, smooth: boolean) => {
    let headerEl: HTMLElement | null = null;
    const modalHost = scrollContainerRef.current?.closest('ion-modal') as HTMLElement | null;
    if (modalHost) {
      headerEl = modalHost.querySelector<HTMLElement>('[data-header-title]');
    }
    if (!headerEl) {
      const contentHost = scrollContainerRef.current?.closest('ion-content') as HTMLElement | null;
      headerEl = contentHost?.querySelector<HTMLElement>('[data-header-title]') ?? document.querySelector<HTMLElement>('[data-header-title]');
    }

    if (smooth) {
      if (headerEl) headerEl.style.transition = 'opacity 0.2s ease-out';
      if (titleCardAnimationRef.current) {
        titleCardAnimationRef.current.style.transition = 'opacity 0.2s ease-out, transform 0.2s ease-out';
      }
      setTimeout(() => {
        if (headerEl) headerEl.style.transition = '';
        if (titleCardAnimationRef.current) titleCardAnimationRef.current.style.transition = '';
      }, 220);
    }

    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleScroll = useScrollSnap(scrollContainerRef, 66, applyProgress);

  const handleReorder = (event: CustomEvent<ItemReorderEventDetail>) => {
    const reordered = event.detail.complete(activeShortcuts) as Shortcut[];
    setActiveShortcutIds(reordered.map(s => s.id));
  };

  const handleToggleShortcut = (shortcutId: string, isActive: boolean) => {
    const newShortcuts = isActive
      ? activeShortcutIds.filter(id => id !== shortcutId)
      : [...activeShortcutIds, shortcutId];
    setActiveShortcutIds(newShortcuts);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(activeShortcutIds);
    } finally {
      setIsSaving(false);
    }
    onDismiss();
  };

  const handleCancel = () => {
    onDismiss();
  };

  // Computed values
  const activeShortcuts = activeShortcutIds
    .map(id => allShortcuts.find(s => s.id === id))
    .filter((shortcut): shortcut is Shortcut => shortcut !== undefined);

  const availableShortcuts = allShortcuts.filter(shortcut =>
    !activeShortcutIds.includes(shortcut.id)
  );

  const groupedAvailableShortcuts = Object.entries(categoryNames).map(([categoryId, categoryName]) => ({
    id: categoryId,
    name: categoryName,
    shortcuts: availableShortcuts.filter(s => s.category === categoryId)
  })).filter(category => category.shortcuts.length > 0);

  return (
    <BasePageWithHeader
      ref={baseHeaderRef}
      title={t('components.ShortcutsEditModal.editShortcuts')}
      toolbarClassName={styles.toolbar}
      smallTitleClassName={styles.smallTitle}
      titleHeaderClassName={styles.titleHeader}
      startButtons={
        <IonButton shape="round" className={styles.cancelButton} onClick={handleCancel} disabled={isSaving}>
          <IonIcon icon={closeOutline} />
        </IonButton>
      }
      endButtons={
        <IonButton
          fill="clear"
          color="primary"
          shape="round"
          onClick={handleSave}
          className={styles.saveButton}
          disabled={isSaving}
        >
          {isSaving ? <IonSpinner name="dots" /> : t('components.ShortcutsEditModal.save')}
        </IonButton>
      }
    >
      <IonContent scrollY={false} className={styles.content}>
        <IonGrid
          ref={scrollContainerRef}
          className={styles.shortcutsContainer}
          onScroll={(e) => {
            const scrollTop = (e.target as HTMLElement).scrollTop || 0;
            if (!rafTick.current) {
              rafTick.current = true;
              requestAnimationFrame(() => {
                handleScroll(scrollTop);
                rafTick.current = false;
              });
            }
          }}
        >
          <div ref={titleCardRef} className={styles.titleCardWrapper}>
            <TitleCard
              ref={titleCardAnimationRef}
              title={t('components.ShortcutsEditModal.editShortcuts')}
              cardClassName={styles.shortcutsTitleCard}
              titleContainerClassName={styles.titleContainer}
              titleClassName={styles.shortcutsTitle}
            />
          </div>

          {/* Active shortcuts card */}
          <IonCard className={styles.sectionCard}>
            <IonList className={styles.shortcutsList}>
              <IonItem lines="none" className={styles.sectionHeader}>
                <IonLabel>
                  <h2>{t('components.ShortcutsEditModal.activeShortcuts')}</h2>
                  <p>{t('components.ShortcutsEditModal.dragToReorderUncheckToRemove')}</p>
                </IonLabel>
              </IonItem>

              <IonReorderGroup disabled={false} onIonReorderEnd={handleReorder}>
                {activeShortcuts.map((shortcut) => (
                  <IonItem key={shortcut.id} lines="none" className={`${styles.shortcutItem} ${styles.shortcutItemActive}`}>
                    <IonCheckbox
                      slot="start"
                      className={styles.shortcutCheckbox}
                      checked={true}
                      onIonChange={() => handleToggleShortcut(shortcut.id, true)}
                    />
                    <IonIcon icon={shortcut.icon} className={styles.shortcutIcon} />
                    <IonLabel>
                      <h3>{shortcut.title}</h3>
                      <p>{categoryNames[shortcut.category]}</p>
                    </IonLabel>
                    <IonReorder slot="end" />
                  </IonItem>
                ))}
              </IonReorderGroup>
            </IonList>
          </IonCard>

          {/* Available shortcuts by category */}
          {groupedAvailableShortcuts.map((category) => (
            <IonCard key={category.id} className={styles.sectionCard}>
              <IonList className={styles.shortcutsList}>
                <IonItem lines="none" className={styles.sectionHeader}>
                  <IonLabel>
                    <h2>{category.name}</h2>
                    <p>{t('components.ShortcutsEditModal.checkToAddToDashboard')}</p>
                  </IonLabel>
                </IonItem>

                {category.shortcuts.map((shortcut) => (
                  <IonItem key={shortcut.id} lines="none" className={styles.shortcutItem}>
                    <IonCheckbox
                      slot="start"
                      className={styles.shortcutCheckbox}
                      checked={false}
                      onIonChange={() => handleToggleShortcut(shortcut.id, false)}
                    />
                    <IonIcon icon={shortcut.icon} className={styles.shortcutIcon} />
                    <IonLabel>
                      <h3>{shortcut.title}</h3>
                      <p>{category.name}</p>
                    </IonLabel>
                  </IonItem>
                ))}
              </IonList>
            </IonCard>
          ))}
        </IonGrid>
      </IonContent>
    </BasePageWithHeader>
  );
};

export default ShortcutsEditModal;
